import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Loader2,
  Settings,
  Users,
  Shield,
  Trash2,
  Plus,
  Pencil,
  Power
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';



export default function AdminPage() {
  const { isAdmin } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    system_name: 'WPLMS TTM',
    allow_registration: true,
    openai_api_key: '',
    default_model: 'gpt-4o-mini'
  });
  const [users, setUsers] = useState([]);
  const [newApiKey, setNewApiKey] = useState('');

  // User Management State
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    password: '',
    is_admin: false,
    is_active: true
  });
  const [passwordForm, setPasswordForm] = useState('');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [isAdmin, navigate]);

  const fetchData = async () => {
    try {
      const [settingsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/admin/settings`),
        axios.get(`${API_URL}/admin/users`)
      ]);
      setSettings(settingsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error(language === 'zh' ? '获取数据失败' : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const updateData = {
        system_name: settings.system_name,
        allow_registration: settings.allow_registration,
        default_model: settings.default_model
      };

      // Only include API key if user entered a new one
      if (newApiKey.trim()) {
        updateData.openai_api_key = newApiKey;
      }

      await axios.put(`${API_URL}/admin/settings`, updateData);
      toast.success(language === 'zh' ? '设置保存成功' : 'Settings saved successfully');
      setNewApiKey('');
      fetchData(); // Refresh to get masked key
    } catch (error) {
      toast.error(language === 'zh' ? '保存失败' : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };



  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/admin/users`, {
        username: userForm.username,
        email: userForm.email,
        password: userForm.password,
        is_admin: userForm.is_admin
      });
      setUsers([...users, response.data]);
      toast.success(language === 'zh' ? '用户已添加' : 'User added');
      setIsAddUserOpen(false);
      setUserForm({ username: '', email: '', password: '', is_admin: false, is_active: true });
    } catch (error) {
      const message = error.response?.data?.detail || (language === 'zh' ? '添加失败' : 'Failed to add user');
      toast.error(message);
    }
  };

  const handleUpdateUserStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API_URL}/admin/users/${userId}`, {
        is_active: !currentStatus
      });
      setUsers(users.map(u => u.id === userId ? { ...u, is_active: !currentStatus } : u));
      toast.success(language === 'zh' ? '用户状态已更新' : 'User status updated');
    } catch (error) {
      toast.error(language === 'zh' ? '更新失败' : 'Update failed');
    }
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setPasswordForm('');
    setIsEditUserOpen(true);
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (!passwordForm) return;

    try {
      await axios.put(`${API_URL}/admin/users/${editingUser.id}`, {
        password: passwordForm
      });
      toast.success(language === 'zh' ? '密码已重置' : 'Password reset successfully');
      setIsEditUserOpen(false);
      setPasswordForm('');
    } catch (error) {
      toast.error(language === 'zh' ? '重置失败' : 'Reset failed');
    }
  };

  const toggleUserAdmin = async (userId, currentStatus) => {
    try {
      await axios.put(`${API_URL}/admin/users/${userId}`, {
        is_admin: !currentStatus
      });
      setUsers(users.map(u => u.id === userId ? { ...u, is_admin: !currentStatus } : u));
      toast.success(language === 'zh' ? '用户权限已更新' : 'User role updated');
    } catch (error) {
      let message = error.response?.data?.detail;
      if (typeof message === 'object') {
        message = JSON.stringify(message);
      }
      message = message || (language === 'zh' ? '更新失败' : 'Update failed');
      toast.error(message);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm(language === 'zh' ? '确定要删除这个用户吗？这将同时删除其所有项目。' : 'Are you sure? This will also delete all their projects.')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`);
      setUsers(users.filter(u => u.id !== userId));
      toast.success(language === 'zh' ? '用户已删除' : 'User deleted');
    } catch (error) {
      let message = error.response?.data?.detail;
      if (typeof message === 'object') {
        message = JSON.stringify(message);
      }
      message = message || (language === 'zh' ? '删除失败' : 'Delete failed');
      toast.error(message);
    }
  };

  if (loading) {
    return (
      <div className="main-layout">
        <Sidebar />
        <main className="content-container flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </main>
      </div>
    );
  }

  return (
    <div className="main-layout" data-testid="admin-page">
      <Sidebar />

      <main className="content-container">
        <div className="p-6 md:p-8 max-w-4xl">
          <h1 className="text-3xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {t('admin.title')}
          </h1>

          {/* System Settings */}
          <div className="settings-section">
            <h3 className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-slate-500" />
              {t('admin.systemSettings')}
            </h3>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t('admin.systemName')}</Label>
                <Input
                  value={settings.system_name}
                  onChange={(e) => setSettings({ ...settings, system_name: e.target.value })}
                  className="max-w-md input-focus-ring"
                  data-testid="system-name-input"
                />
              </div>

              <div className="flex items-center justify-between max-w-md">
                <Label>{t('admin.allowRegistration')}</Label>
                <Switch
                  checked={settings.allow_registration}
                  onCheckedChange={(checked) => setSettings({ ...settings, allow_registration: checked })}
                  data-testid="allow-registration-switch"
                />
              </div>
            </div>
          </div>

          {/* AI Settings */}
          <div className="settings-section">
            <h3 className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-slate-500" />
              {t('admin.aiSettings')}
            </h3>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t('admin.apiKey')}</Label>
                <div className="flex items-center gap-2 max-w-md">
                  <Input
                    type="password"
                    value={newApiKey}
                    onChange={(e) => setNewApiKey(e.target.value)}
                    placeholder={settings.openai_api_key || t('admin.apiKeyPlaceholder')}
                    className="flex-1 input-focus-ring"
                    data-testid="api-key-input"
                  />
                </div>
                {settings.openai_api_key && (
                  <p className="text-xs text-slate-500">
                    {language === 'zh' ? '当前已设置 API Key' : 'API Key is currently set'}: {settings.openai_api_key}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>{t('admin.defaultModel')}</Label>
                <Input
                  value={settings.default_model}
                  onChange={(e) => setSettings({ ...settings, default_model: e.target.value })}
                  placeholder="e.g. gpt-4o"
                  className="max-w-md input-focus-ring"
                  data-testid="default-model-input"
                />
              </div>
            </div>
          </div>

          <Button
            onClick={saveSettings}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700 text-white mb-8"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              t('admin.save')
            )}
          </Button>

          {/* User Management */}
          <div className="settings-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="flex items-center gap-2">
                <Users className="w-5 h-5 text-slate-500" />
                {t('admin.userManagement')}
              </h3>
              <Dialog open={isAddUserOpen} onOpenChange={setIsAddUserOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    {language === 'zh' ? '添加用户' : 'Add User'}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{language === 'zh' ? '添加新用户' : 'Add New User'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddUser} className="space-y-4">
                    <div className="space-y-2">
                      <Label>{t('auth.username')}</Label>
                      <Input
                        value={userForm.username}
                        onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                        required
                        className="input-focus-ring"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('auth.email')}</Label>
                      <Input
                        type="email"
                        value={userForm.email}
                        onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                        required
                        className="input-focus-ring"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('auth.password')}</Label>
                      <Input
                        type="password"
                        value={userForm.password}
                        onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                        required
                        className="input-focus-ring"
                        minLength={6}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={userForm.is_admin}
                        onCheckedChange={(checked) => setUserForm({ ...userForm, is_admin: checked })}
                      />
                      <Label>{t('admin.admin')}</Label>
                    </div>
                    <DialogFooter>
                      <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                        {t('common.save')}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('auth.username')}</TableHead>
                  <TableHead>{t('auth.email')}</TableHead>
                  <TableHead>{t('admin.role')}</TableHead>
                  <TableHead>{language === 'zh' ? '状态' : 'Status'}</TableHead>
                  <TableHead className="text-right">{t('admin.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(user => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell className="font-medium">{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge
                        variant={user.is_admin ? 'default' : 'secondary'}
                        className={user.is_admin ? 'bg-indigo-600' : ''}
                      >
                        {user.is_admin ? t('admin.admin') : t('admin.user')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.is_active ? 'outline' : 'destructive'} className={user.is_active ? "text-green-600 border-green-200 bg-green-50" : ""}>
                        {user.is_active ? (language === 'zh' ? '启用' : 'Active') : (language === 'zh' ? '禁用' : 'Disabled')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUpdateUserStatus(user.id, user.is_active)}
                          className={user.is_active ? "text-green-600" : "text-amber-600"}
                          title={user.is_active ? (language === 'zh' ? '禁用' : 'Disable') : (language === 'zh' ? '启用' : 'Enable')}
                        >
                          <Power className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditUser(user)}
                          title={language === 'zh' ? '重置密码' : 'Reset Password'}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleUserAdmin(user.id, user.is_admin)}
                          data-testid={`toggle-admin-${user.id}`}
                          title={user.is_admin ? (language === 'zh' ? '取消管理员' : 'Remove Admin') : (language === 'zh' ? '设为管理员' : 'Make Admin')}
                        >
                          <Shield className={`w-4 h-4 ${user.is_admin ? 'fill-current' : ''}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteUser(user.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <Dialog open={isEditUserOpen} onOpenChange={setIsEditUserOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {language === 'zh' ? '重置密码' : 'Reset Password'}: {editingUser?.username}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdatePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label>{language === 'zh' ? '新密码' : 'New Password'}</Label>
                  <Input
                    type="password"
                    value={passwordForm}
                    onChange={(e) => setPasswordForm(e.target.value)}
                    required
                    className="input-focus-ring"
                    minLength={6}
                    placeholder={language === 'zh' ? '输入新密码...' : 'Enter new password...'}
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                    {language === 'zh' ? '重置密码' : 'Reset Password'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
}
